import legacyUserIds from "@/assets/legacyUserIds.json";
import userDefaultDocs from "@/assets/userDefaults.json";
import { auth, firestore } from "@/firebase";
import { runOnce } from "@/utils/runOnce";
import { Timestamp, doc, setDoc } from "firebase/firestore";
import { useState } from "react";
import { syncUserProfileToAuth } from "./useUserProile";

export const useInitializeAccount = () => {
  const [isComplete, setIsComplete] = useState(false);

  const start = async () => {
    if (!auth.currentUser) return;

    const runOnceKey = `useInitializeAccount:${auth.currentUser?.uid}`;

    runOnce(
      runOnceKey,
      async () => {
        await Promise.all([syncUserProfileToAuth(), addUserDataDefault()]);

        setIsComplete(true);
      },
      {
        storage: "localStorage",
      },
    );
  };

  return { start, isComplete };
};

// migrate legacy users to new system
// this is only for legacy users who have not been migrated yet
export const useInitializeLegacyAccount = () => {
  if (!auth.currentUser) return;
  if (!legacyUserIds.includes(auth.currentUser.uid)) return;
  const HAS_RUN_ONCE_KEY = "LegacyUserHasRunOnce" + auth.currentUser?.uid;
  const hasRunOnce = localStorage.getItem(HAS_RUN_ONCE_KEY) === "true";
  if (hasRunOnce) return;
  localStorage.setItem(HAS_RUN_ONCE_KEY, "true");
  addUserDataDefault();
};

const addUserDataDefault = async () => {
  if (!auth.currentUser) throw new Error("User not authenticated");

  const oldUserId =
    userDefaultDocs[0].collection === "modelEntityRecordOrganization"
      ? userDefaultDocs[0].id
      : null;

  if (!oldUserId) {
    throw new Error("No old user ID found in user defaults");
  }

  const idMap: { oldId: string; newId: string }[] = [];

  let prevCollection: string | null = null;
  let batch: Promise<void>[] = [];

  for (const template of userDefaultDocs) {
    if (prevCollection !== null && template.collection !== prevCollection) {
      await Promise.all(batch);
      batch = [];
    }

    const oldId = template.id;
    const newId =
      oldId === oldUserId
        ? auth.currentUser.uid
        : createHash(auth.currentUser.uid, oldId);

    idMap.push({ oldId, newId });

    let replacedIdData = JSON.stringify({
      ...template.data,
      creatorId: auth.currentUser.uid,
    });

    idMap.forEach(({ oldId, newId }) => {
      replacedIdData = replacedIdData.split(`"${oldId}"`).join(`"${newId}"`);
    });

    const data = {
      ...JSON.parse(replacedIdData),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),

      // convert date props
      // if establishedAt is present, set it to now
      ...(template.data.establishedAt
        ? { establishedAt: Timestamp.now() }
        : {}),
    };

    const docRef = doc(firestore, template.collection, newId);
    const promise = setDoc(docRef, data);
    batch.push(promise);
    prevCollection = template.collection;

    if (template.revisionData) {
      const revisionDocRef = doc(docRef, "revision", "latest");
      batch.push(setDoc(revisionDocRef, template.revisionData));
    }
  }

  await Promise.all(batch);
};

function createHash(seed: string, input: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }

  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
    result += chars[hash % chars.length];
  }

  return result;
}
